// Quote Builder — save-conflict banner.
//
// Shown when the server REFUSES a save because the quote changed elsewhere
// (last-write-wins guard, 409 stale_write). Both copies still exist at this
// point: the server's is intact because the write was refused, and the user's
// edits are still in the editor and in localStorage. So the banner's whole job
// is to make the user choose, rather than either side being silently lost —
// which is what used to happen, and what emptied KOS-Q-VQCX twice.
//
// Deliberately persistent: no auto-dismiss timer. This is potential data loss,
// not a transient blip, and the quote stays unsaved until the user acts.
//
// Follows the resume-banner pattern (same file, same amber warning language)
// rather than a modal, so the user can still see and copy their own edits while
// deciding. No window.alert/confirm anywhere — house rule.
//
// Defraud / Stripe / commission: untouched. This module only renders DOM.

import { svgIcon } from "./formatters";

const BANNER_ID = "qb-conflict-banner";

export interface ConflictBannerOptions {
  /** Server-supplied explanation, if it sent one. */
  message?: string;
  /** Reload the server copy, discarding local edits. Resolves false if the
   *  fetch failed, in which case the banner stays up. */
  onReload: () => Promise<boolean>;
  /** Called when the user chooses to keep editing (banner dismissed). */
  onKeepEditing?: () => void;
}

export function dismissConflictBanner(): void {
  document.getElementById(BANNER_ID)?.remove();
}

export function showConflictBanner(opts: ConflictBannerOptions): void {
  // Idempotent: repeated failed autosaves must not stack banners. The first one
  // already says everything, and the newest reload closure is equivalent.
  if (document.getElementById(BANNER_ID)) return;

  const banner = document.createElement("div");
  banner.id = BANNER_ID;
  banner.className = "qb-conflict-banner";
  banner.setAttribute("role", "alert");
  banner.setAttribute("aria-live", "assertive");

  const detail = opts.message
    ? String(opts.message)
    : "This quote was changed elsewhere.";

  // Warning triangle + exclamation, 1.5px/24×24 house icon style.
  const icon = svgIcon(
    "M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z|M12 9v4|M12 17h.01",
  );

  banner.innerHTML = `
    <div class="qb-conflict-banner__ic">${icon}</div>
    <div class="qb-conflict-banner__tx">
      <strong>Quote changed elsewhere</strong>
      <span>${escapeHtml(detail)} Your changes have <em>not</em> been saved.</span>
    </div>
    <div class="qb-conflict-banner__actions">
      <button type="button" class="qb-btn qb-btn--primary" data-role="reload">Reload latest</button>
      <button type="button" class="qb-btn" data-role="keep">Keep editing</button>
    </div>
  `;

  const reloadBtn = banner.querySelector<HTMLButtonElement>("[data-role='reload']");
  reloadBtn?.addEventListener("click", () => {
    reloadBtn.disabled = true;
    reloadBtn.textContent = "Reloading…";
    void opts.onReload()
      .then((ok) => {
        if (ok) {
          dismissConflictBanner();
        } else {
          // Could not fetch the server copy — keep the banner and the user's
          // edits rather than leaving them stranded with no way back.
          reloadBtn.disabled = false;
          reloadBtn.textContent = "Reload latest";
        }
      })
      .catch(() => {
        reloadBtn.disabled = false;
        reloadBtn.textContent = "Reload latest";
      });
  });

  banner.querySelector<HTMLButtonElement>("[data-role='keep']")?.addEventListener("click", () => {
    dismissConflictBanner();
    try { opts.onKeepEditing?.(); } catch { /* caller's problem, not ours */ }
  });

  document.body.appendChild(banner);
}

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}
